import { appendRedirectUrl, authReturnTargets } from '../Auth/redirectUrl';
import {
  PUBLIC_PORTAL_PAGE_ROUTE,
  PUBLIC_PORTAL_WIDGET_ROUTE,
  buildPublicPortalPath,
  buildPublicPortalUrl,
  isPublicPortalWidgetPath,
} from '../PublicCatalog/publicPortalRoutes';
import { SUPPLIER_HOME_PATH } from './planAccess';

export const SupplierRoutePath = Object.freeze({
  Home: SUPPLIER_HOME_PATH,
  Company: '/supplier/company',
  Installations: '/supplier/installations',
  EmissionFactors: '/supplier/emission-factors',
  Upload: '/supplier/upload',
  // Optional param: the bare path keeps its existing "most-recent diagram"
  // behavior; a param selects that specific (read-only, immutable) diagram —
  // e.g. a draft's lineage.sourceDiagramId.
  Diagram: '/supplier/diagram/:diagramId?',
  Drafts: '/supplier/drafts',
  DraftReview: '/supplier/drafts/:draftId',
  Share: '/supplier/share',
  Inbox: '/supplier/inbox',
  Embed: '/supplier/embed',
  Request: '/supplier/requests/:requestId',
  RequestUpload: '/supplier/requests/:requestId/upload',
  RequestDirectEntry: '/supplier/requests/:requestId/direct-entry',
});

// `/supplier/diagram` with no id — the default "most-recent installation
// diagram" view.
export const SUPPLIER_DIAGRAM_BASE_PATH = '/supplier/diagram';

export const buildSupplierDiagramPath = (diagramId) =>
  diagramId ? `${SUPPLIER_DIAGRAM_BASE_PATH}/${diagramId}` : SUPPLIER_DIAGRAM_BASE_PATH;

export const buildSupplierDraftReviewPath = (draftId) =>
  SupplierRoutePath.DraftReview.replace(':draftId', draftId);

// B5: one intent-specific builder per request-scoped destination, instead of
// a single `buildSupplierRequestPath(template, id)` that made call sites name
// the route template themselves (and made "which template did I mean" a
// silent typo risk).
export const buildSupplierRequestReviewPath = (requestId) =>
  SupplierRoutePath.Request.replace(':requestId', requestId);

export const buildSupplierRequestUploadPath = (requestId) =>
  SupplierRoutePath.RequestUpload.replace(':requestId', requestId);

export const buildSupplierRequestDirectEntryPath = (requestId) =>
  SupplierRoutePath.RequestDirectEntry.replace(':requestId', requestId);

export const isSupplierInvitationPath = (pathname) =>
  typeof pathname === 'string' && /^\/join\/[^/]+\/?$/.test(pathname);

export const isSupplierHomePath = (pathname) =>
  pathname === '/supplier' || pathname?.startsWith('/supplier/');

// The canonical portal page and compact embed widget. Both are public,
// password-aware surfaces over the same portal configuration and payload.
export const SUPPLIER_PUBLIC_PAGE_PATH = PUBLIC_PORTAL_PAGE_ROUTE;
export const SUPPLIER_PUBLIC_WIDGET_PATH = PUBLIC_PORTAL_WIDGET_ROUTE;

export const buildSupplierPublicPagePath = (slug) => buildPublicPortalPath(slug);
export const buildSupplierPublicWidgetPath = (slug) =>
  buildPublicPortalPath(slug, { surface: 'widget' });

// F6: a bare `startsWith('/public/supplier-')` prefix match also caught
// lookalikes it must not — /public/supplier-foo (no such route),
// /public/supplier-page with no slug, and /public/supplier-page/a/b (extra
// segment) — all of which _app.jsx would then short-circuit into
// <SupplierPublicRoutes />, whose exact Routes (SUPPLIER_PUBLIC_PAGE_PATH /
// SUPPLIER_PUBLIC_WIDGET_PATH above) don't match either, rendering a blank
// page instead of falling through to real app routing / a proper 404.
// Matches exactly the two mounted routes: one non-empty slug segment, an
// optional trailing slash, nothing more.
export const isSupplierPublicPath = isPublicPortalWidgetPath;

// D-S9-9 (fixpack item 17): origin for the ABSOLUTE public Supplier
// Page/widget links shown to the supplier (the copyable slug URL, the embed
// snippet) — ALWAYS window.location.origin, exactly like the platform's
// other share-link features (CbamPortalSettingsModal.jsx, SharePasswordModal.jsx,
// OperatorReportPage.jsx, InstallationSection.jsx). A hardcoded
// app.carbonglance.com fallback broke every whitelabel/self-hosted build's
// links; the running origin is always correct, dev or prod, CG or
// whitelabel. NOT used for in-app relative navigation/iframe `src`s — those
// stay relative (buildSupplierPublicPagePath/buildSupplierPublicWidgetPath
// above) so they work in every environment without depending on this origin
// resolution.
export const supplierPublicAppOrigin = () => window.location.origin;

export const buildSupplierPublicPageUrl = (slug) => buildPublicPortalUrl(slug);

export const buildSupplierPublicWidgetUrl = (slug) =>
  buildPublicPortalUrl(slug, { surface: 'widget' });

export const isSupplierInvitationContextLocation = (pathname, search = '') => {
  if (isSupplierInvitationPath(pathname)) return true;
  return authReturnTargets(pathname, search, isSupplierInvitationPath);
};

export const isSupplierJourneyLocation = (pathname, search = '') =>
  isSupplierHomePath(pathname) || isSupplierInvitationContextLocation(pathname, search);

export const supplierAwareAfterAuthUrl = (redirectUrl) =>
  isSupplierInvitationPath(redirectUrl) ? redirectUrl : appendRedirectUrl('/init', redirectUrl);
